import { useCallback, useRef } from "react";
import {
  LiveFlashKind,
  TRACKED_PRODUCT_FIELD,
  type TrackedProductField,
} from "@/types/frontend-types";
import type {
  FlagLiveFlash,
  ProductDetailBaseline,
  ProductDetailObservationInput,
} from "./session-live-signals.types";

interface UseSessionProductDetailLiveObserverInput {
  flagLiveFlash: FlagLiveFlash;
}

interface SessionProductDetailLiveObserver {
  markProductDetailObserved: (input: ProductDetailObservationInput) => void;
  resetProductDetailObserver: () => void;
}

export function useSessionProductDetailLiveObserver({
  flagLiveFlash,
}: UseSessionProductDetailLiveObserverInput): SessionProductDetailLiveObserver {
  const previousProductDetailRef = useRef<Record<string, ProductDetailBaseline>>(
    {},
  );

  const markProductDetailObserved = useCallback(
    (input: ProductDetailObservationInput): void => {
      const previous = previousProductDetailRef.current[input.productId];
      const nextBaseline = selectProductDetailBaseline(input);
      if (!previous) {
        previousProductDetailRef.current[input.productId] = nextBaseline;
        return;
      }

      const fields = selectChangedProductDetailFields({
        previous,
        next: nextBaseline,
      });
      if (fields.length === 0) return;

      previousProductDetailRef.current[input.productId] = nextBaseline;
      flagLiveFlash({
        kind: LiveFlashKind.Product,
        entityId: input.productId,
        fields,
        scopeProductId: input.productId,
      });
    },
    [flagLiveFlash],
  );

  const resetProductDetailObserver = useCallback((): void => {
    previousProductDetailRef.current = {};
  }, []);

  return { markProductDetailObserved, resetProductDetailObserver };
}

function selectProductDetailBaseline(
  input: ProductDetailObservationInput,
): ProductDetailBaseline {
  return {
    description: input.description,
    definitionOfDone: input.definitionOfDone,
    summary: input.summary,
  };
}

function selectChangedProductDetailFields(input: {
  previous: ProductDetailBaseline;
  next: ProductDetailBaseline;
}): TrackedProductField[] {
  const fields: TrackedProductField[] = [];
  if (input.previous.description !== input.next.description) {
    fields.push(TRACKED_PRODUCT_FIELD.Description);
  }
  if (input.previous.definitionOfDone !== input.next.definitionOfDone) {
    fields.push(TRACKED_PRODUCT_FIELD.DefinitionOfDone);
  }
  if (input.previous.summary !== input.next.summary) {
    fields.push(TRACKED_PRODUCT_FIELD.Summary);
  }
  return fields;
}
